import React, { memo, useCallback, useMemo } from 'react';
import { useStoreon } from 'storeon/react';
import { Section } from 'common/components/section';
import { SectionSubTitle } from 'common/components/section-subtitle';
import { Typography } from 'common/components/typography';
import { useIntersection, useIsomorphicLayoutEffect } from 'common/hooks';
import { LEADS_STORE_NAME } from 'modules/leads';
import { SERVICES_STORE_NAME } from 'modules/services';
import { SHOPS_STORE_NAME } from 'modules/shops';
import { analyticsUtils } from 'utils/analytics-utils';
import { ConsultationForm, FormType, getShopDropdownList, ResultModal } from './components';
import { DEFAULT_SECTION_TEXT } from './consultation-section-constants';
import { info, section, title, text } from './consultation-section-styles';

// Временно: отключаем Домодедово для потолков
const CEILINGS_CATEGORY_ID = 1527;

export type ConsultationSectionProps = {
  channel?: string;
  commentLabel?: string;
  consultationType: string;
  elementBlock: string;
  elementText: string;
  formType?: FormType;
  isCommentVisible?: boolean;
  servicesSale?: boolean;
  sectionRef?: (el: HTMLElement | null) => void;
  sectionTitle: string;
  sectionText?: string;
  subChannel?: string;
  hardcodeComment?: string;
};

/**
 * Секция записи на консультацию
 */
export const ConsultationSection: React.FC<ConsultationSectionProps> = memo(
  ({
    channel,
    commentLabel,
    consultationType,
    elementBlock,
    elementText,
    formType = FormType.Service,
    isCommentVisible = false,
    servicesSale,
    sectionRef,
    sectionTitle,
    sectionText = DEFAULT_SECTION_TEXT,
    subChannel,
    hardcodeComment = undefined,
  }) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement | null>(null);
    const { leads, services, shops } = useStoreon(LEADS_STORE_NAME, SERVICES_STORE_NAME, SHOPS_STORE_NAME);
    const leadRequestStatus = useMemo(() => leads.leadRequestStatus, [leads]);
    const applicationId = useMemo(() => leads.applicationId, [leads]);
    let shopDropdownList = useMemo(() => getShopDropdownList(services?.serviceList, shops?.shopList), [
      services?.serviceList,
      shops?.shopList,
    ]);

    if (services && services.serviceList?.length > 0 && services.serviceList[0].serviceId === CEILINGS_CATEGORY_ID) {
      shopDropdownList = shopDropdownList.filter(store => store.text !== 'Лемана ПРО Домодедово');
    }

    const showEvent = useCallback(() => {
      analyticsUtils.showEvent(
        elementBlock,
        'formConsultation',
        sectionTitle
      );
    }, [elementBlock, sectionTitle]);

    useIntersection(ref, showEvent);

    useIsomorphicLayoutEffect(() => {
      if (leadRequestStatus === 'Created' || leadRequestStatus === 'Conflict') {
        setIsModalOpen(true);
      }

      if (leadRequestStatus === 'Created') {
        analyticsUtils.sendEvent('GA:show', {
          elementType: 'block',
          elementBlock,
          elementText,
          elementName: 'requestSentSuccessful',
          elementRegistrationId: applicationId,
        });
      }
    }, [applicationId, elementBlock, elementText, leadRequestStatus]);

    /**
     * Обработчик закрытия модального окна
     */
    const handleCloseModal = useCallback(() => {
      setIsModalOpen(false);
    }, [setIsModalOpen]);

    return (
      <Section data-form="consultation" className={section} ref={sectionRef}>
        <div className={info} ref={ref}>
          <SectionSubTitle as="p" color="text-invert" className={title} margin="s">
            {sectionTitle}
          </SectionSubTitle>

          <Typography variant="l" size="body_long" className={text} color="text-invert">
            {sectionText}
          </Typography>
        </div>

        <ConsultationForm
          channel={channel}
          commentLabel={commentLabel}
          consultationType={consultationType}
          formType={formType}
          elementBlock={elementBlock}
          elementName={elementText}
          isCommentVisible={isCommentVisible}
          servicesSale={servicesSale}
          shopDropdownList={shopDropdownList}
          subChannel={subChannel}
          hardcodeComment={hardcodeComment}
        />

        <ResultModal leadRequestStatus={leadRequestStatus} isOpen={isModalOpen} onClose={handleCloseModal} />
      </Section>
    );
  }
);
